import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  Flame, Trophy, Star, Moon, Crown, Award, ArrowLeft,
  Calendar, TrendingUp, Coins, CheckCircle2, Circle,
  Smile, Frown, Meh, Heart, Loader2, Sparkles, ChevronRight
} from "lucide-react";

const MOOD_EMOJI = {
  great: { icon: Sparkles, label: "Great", color: "text-emerald-500", bg: "bg-emerald-50", ring: "ring-emerald-200" },
  good: { icon: Smile, label: "Good", color: "text-green-500", bg: "bg-green-50", ring: "ring-green-200" },
  okay: { icon: Meh, label: "Okay", color: "text-amber-500", bg: "bg-amber-50", ring: "ring-amber-200" },
  low: { icon: Frown, label: "Low", color: "text-orange-500", bg: "bg-orange-50", ring: "ring-orange-200" },
  struggling: { icon: Heart, label: "Struggling", color: "text-rose-500", bg: "bg-rose-50", ring: "ring-rose-200" },
};

const MILESTONE_ICONS = { flame: Flame, star: Star, trophy: Trophy, moon: Moon, crown: Crown };

export default function WellnessJourney() {
  const { user } = useAuth();
  const [streakData, setStreakData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [streakRes, historyRes] = await Promise.all([
        apiClient.get("/wellness/streak"),
        apiClient.get(`/wellness/history?page=${historyPage}&limit=15`)
      ]);
      setStreakData(streakRes.data);
      setHistory(historyRes.data.checkins || []);
      setTotalPages(historyRes.data.pages || 1);
    } catch (err) {
      console.error("Failed to load wellness data", err);
    } finally {
      setLoading(false);
    }
  }, [historyPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-spa-terracotta border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const sd = streakData || {};
  const moodDist = sd.mood_distribution || {};
  const heatmap = sd.heatmap || [];
  const milestones = sd.milestones || [];
  const nextMs = sd.next_milestone;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="wellness-journey-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/dashboard" className="p-2 hover:bg-spa-muted rounded-xl transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5 text-spa-moss" />
          </Link>
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss">My Wellness Journey</h1>
            <p className="text-spa-text-secondary text-sm mt-1">Track your consistency and celebrate milestones</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="wellness-stats-grid">
          <div className="spa-card text-center" data-testid="stat-current-streak">
            <div className="w-12 h-12 mx-auto mb-3 bg-orange-100 rounded-2xl flex items-center justify-center">
              <Flame className="w-6 h-6 text-orange-500" />
            </div>
            <p className="font-serif text-3xl text-spa-moss">{sd.streak || 0}</p>
            <p className="text-sm text-spa-text-secondary mt-1">Current Streak</p>
          </div>
          <div className="spa-card text-center" data-testid="stat-longest-streak">
            <div className="w-12 h-12 mx-auto mb-3 bg-amber-100 rounded-2xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-amber-500" />
            </div>
            <p className="font-serif text-3xl text-spa-moss">{sd.longest_streak || 0}</p>
            <p className="text-sm text-spa-text-secondary mt-1">Longest Streak</p>
          </div>
          <div className="spa-card text-center" data-testid="stat-total-checkins">
            <div className="w-12 h-12 mx-auto mb-3 bg-spa-sage/20 rounded-2xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-spa-sage" />
            </div>
            <p className="font-serif text-3xl text-spa-moss">{sd.total_checkins || 0}</p>
            <p className="text-sm text-spa-text-secondary mt-1">Total Check-ins</p>
          </div>
          <div className="spa-card text-center" data-testid="stat-wellness-coins">
            <div className="w-12 h-12 mx-auto mb-3 bg-spa-terracotta/10 rounded-2xl flex items-center justify-center">
              <Award className="w-6 h-6 text-spa-terracotta" />
            </div>
            <p className="font-serif text-3xl text-spa-moss">{sd.total_wellness_coins || 0}</p>
            <p className="text-sm text-spa-text-secondary mt-1">Coins Earned</p>
          </div>
        </div>

        {/* Next Milestone Banner */}
        {nextMs && (
          <div className="spa-card border border-amber-200/40 bg-gradient-to-r from-amber-50/60 to-white" data-testid="next-milestone-banner">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                {(() => { const Ic = MILESTONE_ICONS[nextMs.icon] || Star; return <Ic className="w-7 h-7 text-amber-500" />; })()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-serif text-lg text-spa-moss">{nextMs.label}</p>
                <p className="text-sm text-spa-text-secondary">
                  {nextMs.days_remaining === 0
                    ? "You've reached this milestone!"
                    : `${nextMs.days_remaining} more day${nextMs.days_remaining !== 1 ? "s" : ""} to earn +${nextMs.bonus} bonus coins`}
                </p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-serif text-2xl text-amber-500">+{nextMs.bonus}</p>
                <p className="text-xs text-spa-text-secondary">coins</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="mt-4 h-2 bg-amber-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full transition-all duration-700"
                style={{ width: `${Math.min(100, ((sd.streak || 0) / nextMs.days) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* 30-Day Heatmap + Mood Distribution */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Heatmap */}
          <div className="lg:col-span-2 spa-card" data-testid="wellness-heatmap">
            <h3 className="font-serif text-xl text-spa-moss mb-1">Last 30 Days</h3>
            <p className="text-sm text-spa-text-secondary mb-4">Your daily check-in activity</p>
            <div className="grid grid-cols-10 sm:grid-cols-15 gap-1.5">
              {heatmap.map((day) => {
                const dateObj = new Date(day.date + "T00:00:00");
                const dayNum = dateObj.getDate();
                const isToday = day.date === new Date().toISOString().split("T")[0];
                return (
                  <div
                    key={day.date}
                    className={`relative aspect-square rounded-lg flex items-center justify-center text-[10px] font-medium transition-all
                      ${day.checked_in
                        ? "bg-spa-sage text-white shadow-sm"
                        : "bg-spa-muted text-spa-text-secondary/60"
                      }
                      ${isToday ? "ring-2 ring-spa-terracotta ring-offset-1" : ""}
                    `}
                    title={`${day.date}${day.checked_in ? " - Checked in" : ""}`}
                    data-testid={`heatmap-${day.date}`}
                  >
                    {dayNum}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-spa-text-secondary">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-spa-sage inline-block" /> Checked in</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-spa-muted inline-block" /> Missed</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-spa-muted ring-2 ring-spa-terracotta inline-block" /> Today</span>
            </div>
          </div>

          {/* Mood Distribution */}
          <div className="spa-card" data-testid="mood-distribution">
            <h3 className="font-serif text-xl text-spa-moss mb-1">Mood Overview</h3>
            <p className="text-sm text-spa-text-secondary mb-4">Last 30 days</p>
            {Object.keys(moodDist).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(MOOD_EMOJI).map(([key, config]) => {
                  const count = moodDist[key] || 0;
                  const total = Object.values(moodDist).reduce((a, b) => a + b, 0);
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  const Icon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-3" data-testid={`mood-bar-${key}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-spa-moss">{config.label}</span>
                          <span className="text-xs text-spa-text-secondary">{count} ({pct}%)</span>
                        </div>
                        <div className="h-1.5 bg-spa-muted rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${config.bg.replace("50", "300")} transition-all duration-500`} style={{ width: `${pct}%`, backgroundColor: pct > 0 ? undefined : "transparent" }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-spa-text-secondary text-center py-8">No mood data yet. Add a mood during your daily check-in!</p>
            )}
          </div>
        </div>

        {/* Milestones */}
        <div className="spa-card" data-testid="milestones-section">
          <h3 className="font-serif text-xl text-spa-moss mb-1">Streak Milestones</h3>
          <p className="text-sm text-spa-text-secondary mb-6">Earn bonus SMAHOO Coins by maintaining your streak</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {milestones.map((ms) => {
              const Icon = MILESTONE_ICONS[ms.icon] || Star;
              const achieved = ms.claimed;
              return (
                <div
                  key={ms.days}
                  className={`relative rounded-2xl p-5 text-center border transition-all duration-300
                    ${achieved
                      ? "bg-gradient-to-b from-amber-50 to-white border-amber-200 shadow-sm"
                      : "bg-white border-spa-border/50 opacity-60"
                    }
                  `}
                  data-testid={`milestone-${ms.days}`}
                >
                  {achieved && (
                    <div className="absolute -top-2 -right-2">
                      <CheckCircle2 className="w-5 h-5 text-amber-500 fill-amber-100" />
                    </div>
                  )}
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-2xl flex items-center justify-center ${achieved ? "bg-amber-100" : "bg-spa-muted"}`}>
                    <Icon className={`w-6 h-6 ${achieved ? "text-amber-500" : "text-spa-text-secondary/50"}`} />
                  </div>
                  <p className={`font-serif text-lg ${achieved ? "text-spa-moss" : "text-spa-text-secondary"}`}>{ms.days} Days</p>
                  <p className="text-xs text-spa-text-secondary mt-1">{ms.label}</p>
                  <p className={`text-sm font-medium mt-2 ${achieved ? "text-amber-500" : "text-spa-text-secondary/50"}`}>
                    +{ms.bonus} coins
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Check-in History */}
        <div className="spa-card" data-testid="checkin-history">
          <h3 className="font-serif text-xl text-spa-moss mb-1">Check-in History</h3>
          <p className="text-sm text-spa-text-secondary mb-6">Your recent wellness check-ins</p>

          {history.length > 0 ? (
            <div className="space-y-3">
              {history.map((entry) => {
                const moodConfig = entry.mood ? MOOD_EMOJI[entry.mood] : null;
                const MoodIcon = moodConfig?.icon || Circle;
                const dateStr = new Date(entry.date + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "short", month: "short", day: "numeric"
                });
                return (
                  <div
                    key={entry.checkin_id}
                    className="flex items-center gap-4 p-3 rounded-xl bg-spa-muted/40 hover:bg-spa-muted/70 transition-colors"
                    data-testid={`history-${entry.checkin_id}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${moodConfig?.bg || "bg-spa-sage/20"}`}>
                      <MoodIcon className={`w-5 h-5 ${moodConfig?.color || "text-spa-sage"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-spa-moss">{dateStr}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${entry.type === "reflection" ? "bg-violet-50 text-violet-600" : "bg-spa-sage/10 text-spa-sage"}`}>
                          {entry.type === "reflection" ? "Reflection" : "Quick"}
                        </span>
                      </div>
                      {entry.reflection && (
                        <p className="text-xs text-spa-text-secondary mt-0.5 truncate">{entry.reflection}</p>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-medium text-amber-500">+{entry.coins_earned}</p>
                      <p className="text-[10px] text-spa-text-secondary">coins</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-spa-text-secondary py-8">No check-ins yet. Start your wellness streak today!</p>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                disabled={historyPage <= 1}
                className="px-4 py-2 rounded-xl text-sm font-medium border border-spa-border hover:bg-spa-muted transition-colors disabled:opacity-30"
                data-testid="history-prev"
              >
                Previous
              </button>
              <span className="text-sm text-spa-text-secondary">
                Page {historyPage} of {totalPages}
              </span>
              <button
                onClick={() => setHistoryPage(p => Math.min(totalPages, p + 1))}
                disabled={historyPage >= totalPages}
                className="px-4 py-2 rounded-xl text-sm font-medium border border-spa-border hover:bg-spa-muted transition-colors disabled:opacity-30"
                data-testid="history-next"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
